import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TokenProvider } from './contexts/TokenContext';
import { useAuth } from './contexts/AuthContext';
import { useToken } from './contexts/TokenContext';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import ProfilePage from './pages/ProfilePage';
import Navbar from './components/Navbar';
import WalletGroupsPage from './pages/WalletGroupsPage';
import serviceMonitor from './services/serviceMonitor';
import activeAgentMonitor from './services/activeAgentMonitor';
import './index.css';
import PaymentDistributionPage from './pages/PaymentDistributionPage';
import PaymentDashboard from './components/PaymentDashboard';
import SolanaDistributionPage from './pages/SolanaDistributionPage';
import TokenDistributionPage from './pages/TokenDistributionPage';
import Whitelist from './components/Whitelist';
import IPTokenSelection from './components/IPTokenSelection';
import IPTokenDashboard from './components/IPTokenDashboard';
import DashboardRouter from './components/DashboardRouter';
import DistributionApprovalSuccess from './components/DistributionApprovalSuccess';

// Server status notification component
const ServerStatusNotification = ({ isDown }) => {
  if (!isDown) return null;
  
  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md bg-amber-50 border-l-4 border-amber-500 p-4 rounded shadow-lg">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-amber-800">
            Server connection issues detected. Some features may be unavailable. 
            The application is running in offline mode.
          </p>
        </div>
      </div>
    </div>
  );
};

// Protected route component with error handling
const ProtectedRoute = ({ children }) => {
  const auth = useAuth();
  
  // Start monitoring active agents when user is authenticated
  useEffect(() => {
    if (auth?.isAuthenticated && auth?.user?.id) {
      console.log('[App] User authenticated, starting active agent monitoring');
      activeAgentMonitor.startMonitoring(auth.user.id);
    }
    
    return () => {
      // Don't stop monitoring on unmount - keep it running
      // Only stop when user logs out
    };
  }, [auth?.isAuthenticated, auth?.user?.id]);
  
  if (!auth) {
    return <div className="flex items-center justify-center h-screen">
      <div className="text-red-600">Authentication error. Please refresh the page or log in again.</div>
    </div>;
  }
  
  const { isAuthenticated, loading } = auth;
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
    </div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  return children;
};

// Token-protected route component - requires both authentication and token selection
const TokenProtectedRoute = ({ children }) => {
  const auth = useAuth();
  const { hasToken } = useToken();
  const location = useLocation();
  
  // Start monitoring active agents when user is authenticated
  useEffect(() => {
    if (auth?.isAuthenticated && auth?.user?.id) {
      console.log('[App] User authenticated, starting active agent monitoring');
      activeAgentMonitor.startMonitoring(auth.user.id);
    }
    
    return () => {
      // Don't stop monitoring on unmount - keep it running
      // Only stop when user logs out
    };
  }, [auth?.isAuthenticated, auth?.user?.id]);
  
  if (!auth) {
    return <div className="flex items-center justify-center h-screen">
      <div className="text-red-600">Authentication error. Please refresh the page or log in again.</div>
    </div>;
  }
  
  const { isAuthenticated, loading } = auth;
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
    </div>;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Check if URL contains a token ID (for direct navigation)
  const urlHasToken = location.pathname.match(/\/(dashboard|ai-trading|wallets|solana-distribution|token-distribution|payments)\/([^\/]+)/) ||
                     location.search.includes('token=');
  
  // Allow access if hasToken is true OR if URL suggests a token context
  // Give TokenContext time to process the URL
  if (!hasToken && !urlHasToken) {
    return <Navigate to="/" />;
  }
  
  // Show loading if we're expecting a token but don't have it yet
  if (!hasToken && urlHasToken) {
    return <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
    </div>;
  }
  
  return children;
};

const MainContentWithNavbar = ({ children }) => {
  const auth = useAuth();
  const isLoggedIn = auth?.isAuthenticated;
  
  return isLoggedIn ? (
    <div className="flex h-screen">
      {/* Main content with top navbar */}
      <div className="flex-1 flex flex-col">
        <Navbar className="h-16 flex-shrink-0" />
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  ) : (
    <div className="flex flex-col h-screen">
      <Navbar className="h-16 flex-shrink-0" />
      <div className="flex-1 overflow-auto">
        {children}
      </div>
    </div>
  );
};

function App() {
  const [isServerDown, setIsServerDown] = useState(false);
  
  // Start service monitor when app loads
  useEffect(() => {
    serviceMonitor.start();
    
    // Listen for server status changes
    const unsubscribe = serviceMonitor.onServerStatusChange((isDown) => {
      setIsServerDown(isDown);
    });
    
    // Clean up on unmount
    return () => {
      serviceMonitor.stop();
      unsubscribe();
    };
  }, []);
  
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <TokenProvider>
          {/* Server status notification */}
          <ServerStatusNotification isDown={isServerDown} />
        
        <Routes>
          <Route path="/login" element={
            <MainContentWithNavbar>
              <LoginPage />
            </MainContentWithNavbar>
          } />
          <Route path="/register" element={
            <MainContentWithNavbar>
              <RegisterPage />
            </MainContentWithNavbar>
          } />
          <Route path="/distribution-approval-success" element={
            <DistributionApprovalSuccess />
          } />
          <Route path="/dashboard/:tokenId" element={
            <TokenProtectedRoute>
              <MainContentWithNavbar>
                <DashboardRouter />
              </MainContentWithNavbar>
            </TokenProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <TokenProtectedRoute>
              <MainContentWithNavbar>
                <DashboardRouter />
              </MainContentWithNavbar>
            </TokenProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <MainContentWithNavbar>
                <ProfilePage />
              </MainContentWithNavbar>
            </ProtectedRoute>
          } />

          <Route path="/wallets/:tokenId" element={
            <TokenProtectedRoute>
              <MainContentWithNavbar>
                <WalletGroupsPage />
              </MainContentWithNavbar>
            </TokenProtectedRoute>
          } />

          <Route path="/solana-distribution/:tokenId" element={
            <TokenProtectedRoute>
              <MainContentWithNavbar>
                <SolanaDistributionPage />
              </MainContentWithNavbar>
            </TokenProtectedRoute>
          } />

          <Route path="/token-distribution/:tokenId" element={
            <TokenProtectedRoute>
              <MainContentWithNavbar>
                <TokenDistributionPage />
              </MainContentWithNavbar>
            </TokenProtectedRoute>
          } />

                    <Route path="/payment-distribution" element={
            <TokenProtectedRoute>
              <MainContentWithNavbar>
                <PaymentDistributionPage />
              </MainContentWithNavbar>
            </TokenProtectedRoute>
          } />

          <Route path="/payments" element={
            <TokenProtectedRoute>
              <MainContentWithNavbar>
                <PaymentDashboard />
              </MainContentWithNavbar>
            </TokenProtectedRoute>
          } />
          <Route path="/whitelist" element={
            <ProtectedRoute>
              <MainContentWithNavbar>
                <Whitelist />
              </MainContentWithNavbar>
            </ProtectedRoute>
          } />
          
          {/* AI Trading Route - Old Dashboard with Agents */}
          <Route path="/ai-trading/:tokenId" element={
            <TokenProtectedRoute>
              <MainContentWithNavbar>
                <Dashboard />
              </MainContentWithNavbar>
            </TokenProtectedRoute>
          } />
          
          {/* New IP Token Routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainContentWithNavbar>
                <IPTokenSelection />
              </MainContentWithNavbar>
            </ProtectedRoute>
          } />
          <Route path="/token-dashboard/:tokenId" element={
            <TokenProtectedRoute>
              <MainContentWithNavbar>
                <IPTokenDashboard />
              </MainContentWithNavbar>
            </TokenProtectedRoute>
          } />
          
          <Route path="/old-dashboard" element={<Navigate to="/dashboard" />} />
        </Routes>
          </TokenProvider>
          </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;